/**
 * AuthService — Full authentication lifecycle
 *
 * Covers TODO Phase 13.2:
 *  - Email + password authentication (bcrypt-compatible SHA-256 hash demo)
 *  - Optional MFA (TOTP via authenticator app)
 *  - JWT access token (15-min) + refresh token (7-day, rotated on use)
 *  - Integrates PasswordPolicyService, AccountLockoutService, SessionManager
 *
 * In production: passwords hashed server-side with bcrypt (cost 12).
 * In this client-side demo, SHA-256 is used as a stand-in.
 */

import CryptoJS from 'crypto-js';
import PasswordPolicyService from './PasswordPolicyService';
import AccountLockoutService from './AccountLockoutService';
import SessionManager from './SessionManager';
import MFAService from './MFAService';
import KeyManagementService from './KeyManagementService';
import { logSecurityEvent } from '../../utils/auditLogger';

const USERS_KEY = 'medfast_users';

class AuthService {
  constructor() {
    this._ensureDemoUsers();
  }

  // ── Primary login flow ────────────────────────────────────────────

  async login(username, password) {
    const normalised = String(username).trim().toLowerCase();

    const lockoutStatus = AccountLockoutService.getStatus(normalised);
    if (lockoutStatus.locked) {
      logSecurityEvent('login_blocked_lockout', { username: normalised });
      return {
        success: false,
        error: `Account locked. Try again in ${lockoutStatus.remainingMinutes} minute(s).`,
        locked: true,
        remainingMinutes: lockoutStatus.remainingMinutes,
      };
    }

    const user = this._findUser(normalised);
    if (!user) {
      AccountLockoutService.recordFailure(normalised);
      logSecurityEvent('login_failed_unknown_user', { username: normalised });
      return { success: false, error: 'Invalid username or password' };
    }

    const passwordValid = this._verifyPassword(password, user.passwordHash, user.salt);
    if (!passwordValid) {
      AccountLockoutService.recordFailure(normalised);
      const postStatus = AccountLockoutService.getStatus(normalised);
      logSecurityEvent('login_failed_bad_password', {
        username: normalised,
        attemptsRemaining: 5 - postStatus.failedAttempts,
      });
      return {
        success: false,
        error: 'Invalid username or password',
        attemptsRemaining: Math.max(0, 5 - postStatus.failedAttempts),
      };
    }

    const passwordExpired = PasswordPolicyService.isExpired(user.passwordChangedAt);
    if (passwordExpired) {
      logSecurityEvent('login_password_expired', { username: normalised });
      return {
        success: false,
        error: 'Password expired. Please reset your password.',
        passwordExpired: true,
      };
    }

    if (MFAService.isMFARequired(user.id)) {
      return {
        success: true,
        mfaRequired: true,
        userId: user.id,
        message: 'MFA verification required',
      };
    }

    return this._completeLogin(user);
  }

  async verifyMFA(userId, mfaCode) {
    const result = await MFAService.authenticate(userId, null, mfaCode);
    if (!result.success) {
      logSecurityEvent('mfa_failed', { userId });
      return { success: false, error: result.error || 'Invalid MFA code' };
    }

    const user = this._findUserById(userId);
    if (!user) {
      return { success: false, error: 'User not found' };
    }

    logSecurityEvent('mfa_verified', { userId, method: result.method });
    return this._completeLogin(user);
  }

  _completeLogin(user) {
    AccountLockoutService.reset(user.username);

    const tokens = SessionManager.createSession(user);

    logSecurityEvent('login_success', {
      userId: user.id,
      username: user.username,
      role: user.role,
    });

    const safeUser = {
      id: user.id,
      name: user.name,
      username: user.username,
      role: user.role,
      title: user.title,
      email: user.email,
      phone: user.phone,
      facilities: user.facilities,
      permissions: user.permissions,
      avatar: user.avatar,
      lastLogin: new Date().toISOString(),
      isActive: true,
      mfaEnabled: MFAService.isMFARequired(user.id),
    };

    return {
      success: true,
      user: safeUser,
      tokens,
      mfaRequired: false,
    };
  }

  // ── Logout ────────────────────────────────────────────────────────

  logout(userId) {
    SessionManager.destroySession();
    KeyManagementService.destroy();
    logSecurityEvent('logout', { userId });
    return { success: true };
  }

  // ── Password change ───────────────────────────────────────────────

  changePassword(userId, currentPassword, newPassword) {
    const user = this._findUserById(userId);
    if (!user) return { success: false, error: 'User not found' };

    if (!this._verifyPassword(currentPassword, user.passwordHash, user.salt)) {
      logSecurityEvent('password_change_failed_wrong_current', { userId });
      return { success: false, error: 'Current password is incorrect' };
    }

    const validation = PasswordPolicyService.validate(newPassword);
    if (!validation.valid) {
      return { success: false, error: validation.errors.join('. ') };
    }

    const reuseCheck = PasswordPolicyService.checkReuse(newPassword, user.passwordHistory || []);
    if (!reuseCheck.allowed) {
      return { success: false, error: reuseCheck.error };
    }

    const { hash, salt } = this._hashPassword(newPassword);
    const history = (user.passwordHistory || []).slice(0, 4);
    history.unshift(user.passwordHash);

    user.passwordHash = hash;
    user.salt = salt;
    user.passwordHistory = history;
    user.passwordChangedAt = new Date().toISOString();
    this._saveUser(user);

    logSecurityEvent('password_changed', { userId });
    return { success: true, message: 'Password changed successfully' };
  }

  // ── Step-up authentication ────────────────────────────────────────

  async stepUpAuthenticate(userId, password) {
    const user = this._findUserById(userId);
    if (!user) return { success: false, error: 'User not found' };

    if (!this._verifyPassword(password, user.passwordHash, user.salt)) {
      logSecurityEvent('step_up_auth_failed', { userId });
      return { success: false, error: 'Incorrect password' };
    }

    SessionManager.grantStepUp();
    logSecurityEvent('step_up_auth_success', { userId });
    return { success: true };
  }

  requiresStepUp(action) {
    const STEP_UP_ACTIONS = [
      'tfn_access',
      'medication_signing',
      'agreement_signing',
      'resident_delete',
      'user_role_change',
    ];
    if (!STEP_UP_ACTIONS.includes(action)) return false;
    return !SessionManager.hasStepUp();
  }

  // ── Password hashing (SHA-256 demo; bcrypt in production) ─────────

  _hashPassword(password) {
    const salt = CryptoJS.lib.WordArray.random(128 / 8).toString();
    const hash = CryptoJS.SHA256(password + salt).toString();
    return { hash, salt };
  }

  _verifyPassword(password, storedHash, salt) {
    const hash = CryptoJS.SHA256(password + salt).toString();
    return hash === storedHash;
  }

  // ── User persistence (localStorage demo) ──────────────────────────

  _getUsers() {
    try {
      return JSON.parse(localStorage.getItem(USERS_KEY) || '[]');
    } catch {
      return [];
    }
  }

  _findUser(username) {
    return this._getUsers().find(u => u.username === username);
  }

  _findUserById(id) {
    return this._getUsers().find(u => u.id === id);
  }

  _saveUser(updatedUser) {
    const users = this._getUsers().map(u => (u.id === updatedUser.id ? updatedUser : u));
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
  }

  _ensureDemoUsers() {
    const existing = this._getUsers();
    if (existing.length > 0) return;

    const { hash, salt } = this._hashPassword('GreenApple@26!');
    const demoAdmin = {
      id: 'admin-001',
      name: 'Admin User',
      username: 'admin',
      role: 'admin',
      title: 'System Administrator',
      email: 'admin@medfastcrm.com',
      phone: '+61 400 123 456',
      facilities: ['all'],
      permissions: ['all'],
      avatar: '\u{1F468}\u200D\u{1F4BC}',
      passwordHash: hash,
      salt,
      passwordHistory: [],
      passwordChangedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem(USERS_KEY, JSON.stringify([demoAdmin]));
  }
}

const instance = new AuthService();
export default instance;
